import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Avatar, Box, CircularProgress, List, ListItemAvatar, ListItemButton, ListItemText, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { StateLayer } from '../../theme/color.utils';
import { familyLevelImage } from '../discovery.utils';
import { FAMILY_LEVEL_ATTRIBUTES, familyLevelLabelKey, type FamilyFacet } from './family-level-facets';

const LAST_LEVEL_DEPTH = FAMILY_LEVEL_ATTRIBUTES.length - 1;

interface FamilyLevelListProps {
  items: FamilyFacet[];
  isLoading: boolean;
  depth: number;
  selectedCode?: string;
  onSelect: (code: string) => void;
  onHover?: (code: string) => void;
  onSeeAll?: () => void;
  onSeeAllHover?: () => void;
  seeAllLabel?: string;
}

export default function FamilyLevelList({ items, isLoading, depth, selectedCode, onSelect, onHover, onSeeAll, onSeeAllHover, seeAllLabel }: FamilyLevelListProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'product-family', 'product-classification']);
  const theme = useTheme();
  const canOpenNext = depth < LAST_LEVEL_DEPTH;
  const listItemButtonSx = {
    borderRadius: 0,
    minHeight: 56,
    px: 2,
    color: 'onSurface.main',
    '&:hover, &.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: alpha(theme.palette.onSurface.main, StateLayer.Layer8),
    },
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const itemButtons = items.map((item) => {
    const image = depth === 0 ? familyLevelImage(item.code) : undefined;
    const openNext = (): void => {
      if (onHover && canOpenNext) {
        onHover(item.code);
      }
    };

    return (
      <ListItemButton
        key={item.code}
        selected={item.code === selectedCode}
        onClick={() => onSelect(item.code)}
        onMouseEnter={openNext}
        sx={listItemButtonSx}
      >
        {image && (
          <ListItemAvatar sx={{ minWidth: 0 }}>
            <Avatar src={image} size={48} />
          </ListItemAvatar>
        )}
        <ListItemText>
          <Typography variant="bodyLarge">
            {t(familyLevelLabelKey(item.code, depth))}
          </Typography>
        </ListItemText>
        {canOpenNext && (
          <ChevronRightIcon sx={{ color: 'onSurfaceVariant.main' }} />
        )}
      </ListItemButton>
    );
  });

  return (
    <List sx={{ overflow: 'auto', pt: 1, px: 1 }}>
      {onSeeAll && (
        <ListItemButton
          sx={listItemButtonSx}
          onClick={onSeeAll}
          onMouseEnter={onSeeAllHover}
        >
          <ListItemText>
            <Typography variant="bodyLarge">
              {seeAllLabel || t('common:see_all')}
            </Typography>
          </ListItemText>
        </ListItemButton>
      )}
      {itemButtons}
    </List>
  );
}
